import React, { useLayoutEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../../util/reactIntl';

import { ExternalLink, NamedLink } from '../../../../../components';

import css from './PriorityLinks.module.css';

/**
 * Create component that shows only a single "Post a new listing" link.
 *
 * @param {*} props contains customLinksMenuClass
 * @returns div with only one link inside.
 */
export const CreateListingMenuLink = props => {
  return (
    <div className={props.customLinksMenuClass}>
      <NamedLink name="NewListingPage" className={classNames(css.priorityLink, css.highlight)}>
        <span className={css.priorityLinkLabel}>
          <FormattedMessage id="TopbarDesktop.createListing" />
        </span>
      </NamedLink>
    </div>
  );
};

/**
 * Link component that can be used on TopbarDesktop.
 *
 * @param {*} props containing linkConfig including resolved 'route' params for NamedLink.
 * @returns NamedLink or ExternalLink component based on config.
 */
const PriorityLink = ({ linkConfig }) => {
  const { text, type, href, route, highlight } = linkConfig;
  const classes = classNames(css.priorityLink, { [css.highlight]: highlight });
  const id = `priority-link-${text.toLowerCase().replace(/ /g, '-')}`;

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    return (
      <NamedLink name={name} params={params} to={to} className={classes} id={id}>
        <span className={css.priorityLinkLabel}>{text}</span>
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={classes}>
      <span className={css.priorityLinkLabel}>{text}</span>
    </ExternalLink>
  );
};

/**
 * Create priority links, which are visible on the desktop layout on the Topbar.
 * If space is limited, this doesn't include anything to the Topbar.
 *
 * @param {*} props contains links array and setLinks function
 * @returns container div with priority links included.
 */
const PriorityLinks = props => {
  const containerRef = useRef(null);
  const { links, priorityLinks, setLinks } = props;

  // With this useEffect, we measure the widths of each rendered priority link.
  // This is done once before the real rendering and it's done outside the viewport.
  // Depending on `links` (not just `containerRef`, which never changes) means this
  // re-measures whenever a genuinely new/updated set of links needs it - e.g. when
  // customLinks (fetched asynchronously from hosted CMS content) arrives after the
  // very first render.
  useLayoutEffect(() => {
    // Use an explicit `measured` flag rather than checking truthiness of `width` itself -
    // a legitimately-measured width of 0 is falsy too, which would otherwise cause this
    // effect to re-measure (and call setLinks) forever.
    const isMeasured = links?.[0]?.measured;
    // Guard against links.length === 0 too: reduce() over an empty array still
    // returns a brand new (but content-identical) empty array on every call, which
    // would otherwise re-trigger this effect forever once customLinks is empty.
    if (containerRef.current && !isMeasured && links.length > 0) {
      const linksFromRenderedWrapper = [...containerRef.current.childNodes];
      let cumulatedWidth = 0;
      // Generate an array of link configs with width & cumulatedWidth included
      const linksWithWidths = links.reduce((picked, l, i) => {
        const width = linksFromRenderedWrapper[i]?.offsetWidth || 0;
        cumulatedWidth = cumulatedWidth + width;
        return [...picked, { ...l, width, cumulatedWidth, measured: true }];
      }, []);
      setLinks(linksWithWidths);
    }
  }, [links, setLinks]);

  const isMeasured =
    links?.[0]?.measured && (priorityLinks.length === 0 || priorityLinks?.[0]?.measured);
  const styleWrapper = !!isMeasured
    ? {}
    : {
        style: {
          position: 'absolute',
          top: '-2000px',
          left: '-2000px',
          width: '100%',
          height: 'var(--topbarHeightDesktop)',
          display: 'flex',
          flexDirection: 'row',
        },
      };
  const linkConfigs = isMeasured ? priorityLinks : links;

  // Always render inline in the Topbar tree so SSR and the initial client render match.
  // (Portaling to document.body caused hydration mismatches.)
  return (
    <div className={css.priorityLinkWrapper} {...styleWrapper} ref={containerRef}>
      {linkConfigs.map((linkConfig, index) => {
        return <PriorityLink key={`${linkConfig.text}_${index}`} linkConfig={linkConfig} />;
      })}
    </div>
  );
};

export default PriorityLinks;
